// @flow
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

const Pagination = ({ tableProps, sizePerPageList, pageCounts, pageNumber, totalCount, onPageChange, onPageClick }) => {
    /**
     * pagination count , index
     */
    const [pageCount, setPageCount] = useState(pageCounts);
    const [pageIndex, setPageIndex] = useState(pageNumber - 1);
    const [pageSize, setPageSize] = useState(tableProps.state.pageSize);
    const [inputPage, setInputPage] = useState(pageNumber);

    useEffect(() => {
        setPageCount(pageCounts);
        setPageIndex(pageNumber - 1);
        setInputPage(pageNumber);
    }, [pageCounts, pageNumber]);

    const handlePageInputSubmit = () => {
        const p = Number(inputPage);
        if (!isNaN(p) && p >= 1 && p <= (pageCount || 1)) {
            if (p !== pageNumber && onPageClick) {
                onPageClick(p);
            }
        } else {
            setInputPage(pageNumber);
        }
    };

    /**
     * get filter pages
     */
    const filterPages = useCallback(
        (visiblePages, totalPages) => {
            return visiblePages.filter((page) => page <= pageCount);
        },
        [pageCount]
    );

    /**
     * handle visible pages
     */
    const getVisiblePages = useCallback(
        (page, total) => {
            if (total < 7) {
                return filterPages([1, 2, 3, 4, 5, 6], total);
            } else {
                if (page % 5 >= 0 && page > 4 && page + 2 < total) {
                    return [1, page - 1, page, page + 1, total];
                } else if (page % 5 >= 0 && page > 4 && page + 2 >= total) {
                    return [1, total - 3, total - 2, total - 1, total];
                } else {
                    return [1, 2, 3, 4, 5, total];
                }
            }
        },
        [filterPages]
    );

    /**
     * handle page change
     * @param page - current page
     * @returns
     */
    const changePage = (page) => {
        const activePage = pageIndex + 1;

        if (page === activePage) {
            return;
        }

        const visiblePages = getVisiblePages(page, pageCounts);
        setVisiblePages(filterPages(visiblePages, pageCounts));
        onPageClick(page);
        // tableProps.gotoPage(page - 1);
    };

    useEffect(() => {
        const visiblePages = getVisiblePages(0, pageCounts);
        setVisiblePages(visiblePages);
    }, [pageCount, getVisiblePages]);

    const [visiblePages, setVisiblePages] = useState(getVisiblePages(0, pageCounts));
    const activePage = pageIndex + 1;

    return (
        <div className="d-lg-flex align-items-center text-center pb-1">
            {sizePerPageList.length > 0 && (
                <div className="d-inline-block me-3">
                    <label className="me-1">Display :</label>
                    <select
                        value={tableProps.state.pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            onPageChange(Number(e.target.value));
                            tableProps.setPageSize(Number(e.target.value));
                        }}
                        className="form-select d-inline-block w-auto">
                        {(sizePerPageList || []).map((pageSize, index) => {
                            return (
                                <option key={index} value={pageSize.value}>
                                    {pageSize.text}
                                </option>
                            );
                        })}
                    </select>{' '}
                    <label className="me-1"> entries</label>
                </div>
            )}

            <span className="me-3">  
                Showing{' '}
                <strong>
                    {tableProps.data.length > 0
                        ? (pageNumber - 1) * pageSize + 1
                        : 0}{" "} to {(pageNumber - 1) * pageSize + 1 * tableProps.data.length} of{' '}
                    {totalCount}
                    {/* {pageIndex + 1} to {tableProps.data.length} of {totalCount} */}
                </strong>{' '}
                entries
            </span>

            <span className="d-inline-flex align-items-center ms-2 me-3 my-sm-0 my-1">
                <label className="me-1 mb-0 text-nowrap" style={{ fontSize: '13px' }}>Go to page:</label>
                <input
                    type="number"
                    min="1"
                    max={pageCount || 1}
                    value={inputPage}
                    onChange={(e) => setInputPage(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handlePageInputSubmit();
                        }
                    }}
                    onBlur={handlePageInputSubmit}
                    className="form-control form-control-sm d-inline-block"
                    style={{ width: '70px', height: '31px' }}
                />
                <span className="ms-1 text-nowrap text-muted" style={{ fontSize: '13px' }}>/ {pageCount}</span>
            </span>

            <ul className="pagination pagination-rounded d-inline-flex ms-auto align-item-center mb-0">
                <li
                    key="prevpage"
                    className={classNames('page-item', 'paginate_button', 'previous', {
                        disabled: activePage === 1,
                    })}
                    onClick={() => {
                        if (activePage === 1) return;
                        changePage(activePage - 1);
                    }}>
                    <Link to="#" className="page-link">
                        <i className="mdi mdi-chevron-left"></i>
                    </Link>
                </li>
                {(visiblePages || []).map((page, index, array) => {
                    return array[index - 1] + 1 < page ? (
                        <React.Fragment key={page}>
                            <li className="page-item disabled d-none d-xl-inline-block">
                                <Link to="#" className="page-link">
                                    ...
                                </Link>
                            </li>
                            <li
                                className={classNames('page-item', 'd-none', 'd-xl-inline-block', {
                                    active: activePage === page,
                                })}
                                onClick={(e) => changePage(page)}>
                                <Link to="#" className="page-link">
                                    {page}
                                </Link>
                            </li>
                        </React.Fragment>
                    ) : (
                        <li
                            key={page}
                            className={classNames('page-item', 'd-none', 'd-xl-inline-block', {
                                active: activePage === page,
                            })}
                            onClick={(e) => changePage(page)}>
                            <Link to="#" className="page-link">
                                {page}
                            </Link>
                        </li>
                    );
                })}
                <li
                    key="nextpage"
                    className={classNames('page-item', 'paginate_button', 'next', {
                        disabled: activePage === pageCounts,
                    })}
                    onClick={() => {
                        if (activePage === pageCounts) return;
                        changePage(activePage + 1);
                    }}>
                    <Link to="#" className="page-link">
                        <i className="mdi mdi-chevron-right"></i>
                    </Link>
                </li>
            </ul>
        </div>
    );
};

export default Pagination;
